import { Avatar, AvatarBadge, AvatarGroup, Box, Button, Flex, HStack, IconButton, Image, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay, Popover, PopoverContent, PopoverTrigger, Spinner, Text, WrapItem, useToast } from "@chakra-ui/react";
import "@cyntler/react-doc-viewer/dist/index.css";
import { faCommentAlt } from "@fortawesome/free-regular-svg-icons";
import { faEdit, faEllipsisV, faEllipsisVertical, faTrash, faVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import _, { capitalize } from 'lodash';
import moment from "moment";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { TbLoader3 } from "react-icons/tb";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import uppyUtility from "../../../config/uppi";
import { AppContext } from "../../../contexts/AppContext";
import { ChatContext } from "../../../contexts/ChatContexts";
import useIsMe from "../../../hooks/useIsMe";
import { fetchCallProviders } from "../../../store/callProviderSlice";
import { setChannel } from "../../../store/channelSlice";
import { setToken } from "../../../store/tokenSlice";
import HTMLComponent from '../../HTMLComponent';
import { useSocket } from "../../Socket";
import '../../style.css';
import MessageInput from "../MessageInput";
import FileViewer from "./FileViewer";
import MessageFileItemPreview from "./MessageFileItemPreview";

const ICONS = {
    Zoom: "/assets/icons/zoom-icon.png",
    VideoSDK: "/assets/icons/videosdk-icon.png",
};


function MessageHistoryView() {
    const { socket, callCreatedData } = useSocket();
    const params = useParams();
    const uppy = useRef(uppyUtility).current;
    let renderRef = useRef(0).current;
    const { channelId, channelType } = params;
    const [chatState, __, { fetchNextPage, refetchUnreadCount }] = useContext(ChatContext);
    const { messageHistory, loaders: { areMessagesLoading, areMessagesFetching } } = chatState;
    const [appState] = useContext(AppContext);
    const [files, setFiles] = useState([]);
    const [call, setCall] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const { isToken } = useSelector((state) => state.token);
    const { providers } = useSelector((state) => state.callProvider);
    const enabledProviders = useMemo(() => providers?.filter(p => p.enabled), [providers]);

    const [editingMessageId, setEditingMessageId] = useState(null);
    dispatch(setToken({ isToken: isToken, chatType: channelType }));
    useEffect(() => {
        if (socket && appState?.user?._id) {
            socket.emit('CHAT_NOTIFICATION_READ', {
                chatType: capitalize(channelType) + 's',
                userId: appState?.user?._id,
                chatId: channelId,
            });
            refetchUnreadCount();
        }
    }, [socket, appState?.user?._id, channelId, channelType, messageHistory]);

    useEffect(() => {
        dispatch(fetchCallProviders());
    }, [dispatch]);

    useEffect(() => {
        if (socket) {
            socket?.on("UPDATED_CALL_PROVIDERS", () => {
                dispatch(fetchCallProviders());
            });
        }
    }, [socket, dispatch]);

    useEffect(() => {
        renderRef++;

        if (renderRef > 1) {
            return;
        }

        uppy.on('files-added', (files) => {
            setFiles((prevFiles) => [...prevFiles, ...files]);
        });

        uppy.on('upload-success', (file, response) => {
            setFiles((prevFiles) =>
                prevFiles?.map(fileObj => {
                    if (fileObj.id === file.id) {
                        return file;
                    }

                    return fileObj;
                })
            );
        });

        uppy.on('upload-error', (file, error, response) => {
            setFiles((prevFiles) =>
                prevFiles?.map(fileObj => {
                    if (fileObj.id === file.id) {
                        return file;
                    }

                    return fileObj;
                })
            );
        });

        uppy.on('complete', (result) => {
            const uploadedFiles = result.successful;
            const failedFiles = result.failed;
            setFiles((prevFiles) => {
                return prevFiles?.map(fileObj => {
                    const updatedFile = uploadedFiles.find(uf => uf.id === fileObj.id) || failedFiles.find(ff => ff.id || fileObj.id);
                    if (updatedFile) {
                        return updatedFile;
                    }

                    return fileObj;
                });
            }
            );
        });

        uppy.on('file-removed', (file) => {
            setFiles((prevFiles) =>
                prevFiles.filter(pf => pf.id !== file.id)
            );
        });

        return () => {
            uppy.off();
        };
    }, []);

    useEffect(() => {
        if (callCreatedData?.roomId && call) {
            const callDetails = {
                roomId: callCreatedData.roomId,
                token: callCreatedData.token,
                type: callCreatedData.callProvider
            };
            const callData = encodeURIComponent(JSON.stringify(callDetails));
            setCall(false);
            setIsLoading(false);
            window.open(`/call/${callCreatedData.roomId}?data=${callData}`, '_blank');
        }
    }, [callCreatedData]);

    const onDrop = useCallback((acceptedFiles) => {
        uppy.addFiles(acceptedFiles);
    }, []);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop, 
        noClick: true
    });
    const selectedChannel = useMemo(() => {
        const find = (channelType === 'channel' ? chatState.channels : chatState.directs).find(item => item._id === channelId);

        return find;
    }, [chatState, channelId, channelType]);

    const getDirectName = useCallback((direct) => {
        const withoutMe = direct?.members?.filter(member => member.user?._id !== appState?.user?._id);
        return withoutMe?.map(member => member?.user?.name).join(", ");
    }, []);

    const getDirectAvatar = useCallback((direct) => {
        const withoutMe = direct?.members?.filter(member => member.user?._id !== appState?.user?._id);
        return withoutMe?.map(member => <Avatar sx={{ img: { objectFit: 'contain' } }} name={member?.user?.name} src={member?.user?.profilePicture} border={'1px solid gray.300'} />);
    }, []);

    const renderChannelAvatar = useCallback(() => {
        if (selectedChannel) {
            if (channelType === 'channel') {
                return <Avatar name={selectedChannel.name} height="40px" width="40px" src={selectedChannel.iconURL} />;
            }

            return (
                <AvatarGroup max={1} size="md">
                    {getDirectAvatar(selectedChannel)}
                </AvatarGroup>
            );
        }

        return 'N/A';
    }, [selectedChannel, channelType]);

    const handlePageChange = useCallback(() => {
        fetchNextPage();
    }, [fetchNextPage]);

    const channelName = useMemo(() => {
        if (selectedChannel) {
            if (channelType === 'channel') {
                return selectedChannel.name;
            }

            return getDirectName(selectedChannel);
        }

        return 'N/A';
    }, [selectedChannel, channelType]);

    const channelMembersNames = useMemo(() =>
        selectedChannel?.members?.map(member => _.capitalize(member?.user?.name)).filter(m => m).join(", "),
        [selectedChannel]);

    const channelMessages = useMemo(() => {
        const allMessages = messageHistory?.[channelId]?.messages || [];
        const groupedByDate = _.groupBy(allMessages, (item) => moment(item.createdAt).format('LL'));
        const groupedByDateAndTime = {};

        Object.keys(groupedByDate).map(key => {
            groupedByDateAndTime[key] = {};

            const groupedByTime = _.groupBy(groupedByDate[key], (item) => `${moment(item.createdAt).format('LT')}`);
            groupedByDateAndTime[key] = groupedByTime;
        });

        let sortedData = _(groupedByDateAndTime)
            .toPairs()
            .orderBy([([date]) => new Date(date)], ['asc'])
            .fromPairs()
            .value();

        return sortedData;
    }, [channelId, chatState.messageHistory, areMessagesFetching, areMessagesLoading]);

    if (!channelId || !channelType || !selectedChannel) {
        return (
            <Box bg="transparent" margin="35px" width="100%" flex={'1 1 auto'}>
            </Box>
        );
    }

    if (!socket) {
        console.error('Socket is not defined');
        return;
    }

    // a function to create a VideoSDK call
    const createVideoSDKCall = async (provider) => {
        await socket.emit("CALL_CREATE", { directId: channelId, type: channelType, channelId: channelId, provider });
        setCall(true);
        setIsLoading(true);
    };

    // a function to crate zoom call
    const createZoomVideoCall = async (provider) => {
        await socket.emit("CALL_CREATE", { directId: channelId, type: channelType, channelId: channelId, provider });
        setCall(true);
        setIsLoading(true);
    };

    return (
        <Box margin="35px" width="100%" {...getRootProps()}>
            <Flex flexDir={'column'} px="30px" py="25px" background="white" borderRadius="br.10" h={'100%'} boxShadow={'0 0 10px 0px rgba(0, 0, 0, 0.1)'}>
                {/* Header Section */}
                <Flex justifyContent="space-between" alignItems="center" mb="12px">
                    <Flex alignItems="center" gap="15px" flex={1} minWidth={0}>
                        <Box flexShrink={0}>
                            <WrapItem>
                                {renderChannelAvatar()}
                            </WrapItem>
                        </Box>
                        <Box flex={1} minWidth={0}>
                            <Text fontSize="fs.16" fontWeight="fw.500" noOfLines={1}>{channelName}</Text>
                            {
                                channelType === "channel" &&
                                <Text
                                    fontSize="fs.12"
                                    fontWeight="fw.400"
                                    noOfLines={1}
                                    textOverflow="ellipsis"
                                    overflow="hidden"
                                    width="100%"
                                >
                                    {channelMembersNames}
                                </Text>
                            }
                        </Box>
                    </Flex>
                    <Flex alignItems="center" gap="16px" flexShrink={0}>
                        <Menu>
                            <MenuButton
                                width="30px"
                                height="30px"
                                alignItems="center"
                                justifyContent="center"
                                background="#2D8CFF"
                                borderRadius="br.4"
                                cursor="pointer"
                                _hover={{ opacity: 0.9 }}
                            >
                                {isLoading ? (
                                    <TbLoader3 className="animate-spin text-xl text-white ml-1" />
                                ) : (
                                    <FontAwesomeIcon icon={faVideo} style={{ color: "white" }} />
                                )}
                            </MenuButton>

                            <MenuList zIndex={9999}>
                                {enabledProviders && enabledProviders.length > 0 ? (
                                    enabledProviders.map((provider) => (
                                        <MenuItem
                                            key={provider._id}
                                            onClick={async () => {
                                                const callCreators = {
                                                    Zoom: createZoomVideoCall,
                                                    VideoSDK: createVideoSDKCall,
                                                };

                                                const callCreator = callCreators[provider.name];
                                                if (callCreator) {
                                                    await callCreator(provider.name);
                                                }
                                            }}
                                        >
                                            <Flex align="center" gap={1}>
                                                <Image
                                                    src={ICONS[provider.name] || "/assets/icons/default.png"}
                                                    alt={provider.name}
                                                    boxSize="26px"
                                                    objectFit="contain"
                                                    borderRadius="full"
                                                    background="#eee"
                                                />
                                                {provider.name}
                                            </Flex>
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem isDisabled>
                                        No provider is enabled
                                    </MenuItem>
                                )}
                            </MenuList>
                        </Menu>

                        <FontAwesomeIcon style={{
                            marginLeft: "10px"
                        }} icon={faEllipsisVertical} />
                    </Flex>
                </Flex>
                {/* Chat history */}
                <Flex flexDir={'column'} pos={"relative"} flex={'1 1 auto'} overflow={'hidden'} zIndex={1}>
                    <Box flex="1 1 auto" id="chat-history-scroller" display={'flex'} flexDirection="column-reverse" borderTop="1px solid #E7E8EA" overflowY="scroll" pb="15px">
                        <InfiniteScroll
                            dataLength={messageHistory?.[channelId]?.messages?.length || 0}
                            next={handlePageChange}
                            hasMore={messageHistory?.[channelId]?.messages?.length < messageHistory?.[channelId]?.total}
                            pullDownToRefresh={false}
                            inverse
                            style={{ display: 'flex', flexDirection: 'column-reverse', position: 'relative', overflowY: 'auto', flex: '1 1 auto' }}
                            scrollableTarget="chat-history-scroller"
                            loader={<Text textAlign={'center'}><Spinner my="2.5" size={'lg'} color="purple.500" /></Text>}
                            scrollThreshold={0.25}
                        >
                            {
                                (areMessagesLoading || areMessagesFetching) && <Flex py={3} justify={'center'}><Spinner size="md" color="purple.500" /></Flex>
                            }
                            <MessageGroupByDate
                                channelMessages={channelMessages}
                                editingMessageId={editingMessageId}
                                setEditingMessageId={setEditingMessageId}

                            />
                            <Box id="anchor" style={{ overflowAnchor: 'auto' }} />
                        </InfiniteScroll>
                    </Box>
                    <Box>
                        <input {...getInputProps()} />
                        <MessageInput files={files} openFileDialog={open} />
                    </Box>
                    {
                        isDragActive &&
                        <Box pos={'absolute'} top="0" left="0" w="100%" h="100%" borderRadius={10} bg="white" zIndex={5} border={'1px dashed gray'}>
                            <Flex h="100%" flexDir={'column'} justify={'center'} align={'center'} rowGap={2}>
                                <HStack>
                                    <Image src="/assets/icons/docx.png" w={50} h={50} />
                                    <Image src="/assets/icons/pdf.png" w={50} h={50} />
                                    <Image src="/assets/icons/ppt.png" w={50} h={50} />
                                    <Image src="/assets/icons/xls.png" w={50} h={50} />
                                </HStack>
                                <Text fontSize={'2xl'}>Drop files here</Text>
                            </Flex>
                        </Box>
                    }
                </Flex>
            </Flex>
            <Modal size={'xl'}>
                <ModalOverlay />
                <ModalContent>
                    <ModalCloseButton border="1px solid #868B944D" />
                    <ModalBody pos={'relative'}>
                        {/* <DocViewer documents={imgs} pluginRenderers={DocViewerRenderers} />; */}
                        <Button
                            variant={'unstyled'} p={2} bg={'white'} borderRadius={'100%'}
                            pos={'absolute'} left={0} top={'50%'}
                        >
                            <IconArrowLeft stroke={2} size={24} />
                        </Button>
                        <Button
                            variant={'unstyled'} p={2} bg={'white'} borderRadius={'100%'}
                            pos={'absolute'} right={0} top={'50%'}
                        >
                            <IconArrowRight stroke={2} size={24} />
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default memo(MessageHistoryView);

const MessageGroupByDate = memo((props) => {
    const { channelMessages, editingMessageId, setEditingMessageId, } = props || {};
    return Object.keys(channelMessages)?.reverse()?.map?.((dateKey) =>
        <Box key={'message_group_date_' + dateKey}>
            <Flex pos={"relative"} justify={'center'} p={2} bottom={0} >
                <Text pos={"relative"} zIndex={1} bg="gray.100" fontSize={'12'} fontWeight={'500'} py={2} px={4} borderRadius={'20px'}>{dateKey}</Text>
                <Box pos={'absolute'} w={"100%"} left="0" top="50%" borderTop="2px solid" borderColor={'gray.200'} zIndex={0} />
            </Flex>
            {
                Object.keys(channelMessages?.[dateKey])?.map((timeAndUserKey) =>
                    <Box key={'message_time_key_' + timeAndUserKey}>
                        {
                            channelMessages[dateKey][timeAndUserKey]?.map((message, index) =>
                                <MessageItem
                                    key={'message_item_' + message?._id}
                                    message={message}
                                    isPreviousMessageIsMine={message?.senderId?._id === channelMessages[dateKey][timeAndUserKey]?.[index - 1]?.senderId?._id}
                                    setEditingMessageId={setEditingMessageId}
                                    isEditing={editingMessageId === message._id}
                                    editingModeActive={editingMessageId !== null}
                                    onEdit={() => setEditingMessageId(message._id)}
                                    onCancel={() => setEditingMessageId(null)}

                                />
                            )
                        }
                    </Box>
                )
            }
        </Box>
    );
}, (pp, np) => _.isEqual(pp, np));

const CallStatusMessage = ({ _id, messageTime, callStatus, roomId, duration, callProvider }) => {
    const [joinCall, setJoinCall] = useState(false);
    const { socket, callEndStatus, clearCallEndStatus, tokenData } = useSocket();
    const dispatch = useDispatch();

    useEffect(() => {
        if (callEndStatus?.callStatus === "Call ended") {
            clearCallEndStatus();
        }
    }, [callEndStatus]);

    useEffect(() => {
        if (joinCall) {
            const callDetails = {
                roomId: tokenData.roomId,
                token: tokenData.token,
                type: callProvider
            };

            const callData = encodeURIComponent(JSON.stringify(callDetails));
            dispatch(setToken({ isToken: tokenData?.token }));
            window.open(`/call/${tokenData?.roomId}?data=${callData}`, '_blank');
            setJoinCall(false);
        }
    }, [tokenData]);

    const JoinCallHandler = () => {
        socket.emit("GENERATE_VIDEO_SDK_TOKEN", { roomId: roomId, callProvider: callProvider });
        setJoinCall(true);
    };

    return (
        <div
            className="max-w-xs mx-auto p-4 bg-[#edf2f7] shadow-lg rounded-xl flex items-center justify-between space-x-4 mb-3"
        >
            <div className="flex items-center space-x-4">
                <div>
                    {_id !== callEndStatus?._id && (
                        <>
                            <p className='text-sm text-black'>{callStatus}</p>
                            <p className="text-base font-medium text-black capitalize">{messageTime} {duration && `| ${duration}`}</p>
                        </>
                    )}
                    {_id === callEndStatus?._id && (
                        <>
                            <p className='text-sm text-black'>{callEndStatus?.callStatus}</p>
                            <p className="text-base font-medium text-black capitalize">{messageTime} {callEndStatus?.duration && `| ${callEndStatus?.duration}`}</p>
                        </>
                    )}

                </div>
            </div>
            {(callStatus === "In Progress") &&
                <div className={`flex space-x- ${_id === callEndStatus?._id && "hidden"}`}>
                    <button
                        onClick={JoinCallHandler}
                        className="p-2 focus:outline-none text-sm font-semibold focus:ring-2 focus:ring-green-400 bg-green-500 text-white rounded-full hover:bg-green-600"
                    >
                        Join Call
                    </button>
                </div>}
        </div>
    );
};

const MessageItem = memo((props) => {
    const toast = useToast();
    // const [activeDoc, setActiveDoc] = useState(0);
    const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
    const [appState] = useContext(AppContext);
    const [chatState, , { deleteMessage }] = useContext(ChatContext);
    const { message, isPreviousMessageIsMine, isEditing, setEditingMessageId, editingModeActive, onCancel, messageId, openFileDialog } = props || {};
    const { fileURL, senderId, text, createdAt, type, duration, callStatus, _id, roomId, callProvider } = message;
    const isMe = useIsMe(senderId._id);
    const navigate = useNavigate();
    const { onlineUsers, socket } = useSocket();
    const dispatch = useDispatch();
    const [isEdit, setIsEdit] = useState(false);

    const [files, setFiles] = useState([]);
    const messageTime = useMemo(() => {
        return moment(createdAt).format('LT');
    }, [createdAt]);
    dispatch(setChannel({ userName: appState?.user?.name }));

    const directId = useMemo(() => {
        const direct = chatState.directs
            .find(direct =>
                direct?.members?.length === 2 &&
                direct?.members?.find?.(member => member?.user?._id === senderId?._id) &&
                direct?.members?.find?.(member => member?.user?._id === appState?.user?._id));

        return direct?._id;
    }, [message, chatState.directs]);

    const redirectToDirects = useCallback(() => {
        if (directId) {
            navigate(`/message/direct/${directId}`);
        }
        else {
            socket.emit('DIRECT_CREATE', {
                userIds: [senderId?._id, appState?.user?._id]
            }, (response) => {
                const directId = response?.data?._id;
                if (directId) {
                    navigate(`/message/direct/${directId}`);
                } else {
                    toast({
                        title: 'Error',
                        description: "We are having problems creating a direct message.",
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                }
            });
        }
    }
        , [directId, socket, senderId, appState?.user?._id, navigate, toast]);

    const isUserOnline = useMemo(() => {
        return onlineUsers[senderId?._id] || false;
    }, [onlineUsers, senderId]);

    const handleEditMessage = (messageId) => {
        setEditingMessageId(messageId);
    };

    const handleDeleteMessage = async (messageId) => {
        console.log("deleted messageId", messageId);
        try {
            await deleteMessage(messageId, "deleted");
        } catch (err) {
            console.error("Error deleting message:", err);
        }
    }; 
    console.log("MessageItem message:", message);

    console.log('text======>>>>>', text);
    return (
        <Box className="test" pos={'relative'} mt={!isPreviousMessageIsMine ? 3 : 0} role="group" W="100%" overflow={'visible'} flex={'1 1 auto'} _hover={{ bg: "gray.50" }}>
            <Box display={'block'} justify={` ${isEditing ? 'none' : 'space-between'}`} >
                <Popover placement='top' trigger="hover" isLazy>
                    <Flex width="100%">
                        <Flex mt="5px" flexDir={'row'} gap="15px" width={'100%'}>
                            <>
                                <PopoverTrigger>
                                    {
                                        isPreviousMessageIsMine ?
                                            <Box h={45} w={45} visibility={'hidden'} />
                                            :
                                            <Box>
                                                <Avatar height="45px" width="45px" name={senderId.name} src={senderId.profilePicture}>
                                                    <AvatarBadge boxSize='1em' bg={isUserOnline ? 'green.500' : 'gray.500'} />
                                                </Avatar>
                                            </Box>
                                    }
                                </PopoverTrigger>

                                {
                                    isEditing ? <Box w="100%">
                                        <MessageInput files={fileURL} valueProps={text} messageId={message._id} onCancel={onCancel} />
                                    </Box> :
                                        <Box>
                                            {
                                                !isPreviousMessageIsMine &&
                                                <Flex alignItems="center" gap="10px" flexDir={'row'} >
                                                    <Text fontSize="fs.14" textAlign="end" fontWeight="fw.600">{senderId.name}</Text>
                                                    <Text fontSize="fs.12" textAlign="start" fontWeight="fw.400" color="#868B94">{messageTime}</Text>
                                                </Flex>
                                            }
                                            {
                                                text &&
                                                <Flex alignItems={"flex-end"} flexWrap={"wrap"} gap={"4px"}>
                                                        <Flex className="message-preview" w={'fit-content'} flexDir={'column'} gap="6px" py="10px" px="15px" background="gray.100" borderRadius="0px 12px 12px 12px">
                                                            <HTMLComponent htmlContent={text} />
                                                        </Flex>
                                                    {message.isEdited && <Text as="span" fontSize="xs" color="gray.500">(edited)</Text>}
                                                </Flex>

                                            }
                                            {
                                                Array.isArray(fileURL) &&
                                                fileURL.map(file =>
                                                    <Flex className="~~~~~~~~~~" my={1} w={'fit-content'} flexDir={'column'} gap="6px" py="10px" px="15px" background="gray.100" borderRadius="0px 12px 12px 12px">
                                                        <Box key={file} my={1}>
                                                            <MessageFileItemPreview file={file} openDocViewer={() => setIsDocViewerOpen(true)} />
                                                        </Box>
                                                    </Flex>
                                                )
                                            }
                                        </Box>
                                }
                            </>
                        </Flex>
                    </Flex>
                    <PopoverContent flexDir={'column'} gap={'10px'} p={4} w={'fit-content'} borderRadius={'10px'} boxShadow={'0 0 5px 2px rgba(0, 0, 0, 0.2)'} ml={'10px'}>
                        <Flex flexDir={'row'} align={'center'} columnGap={'10px'}>
                            <Avatar height="45px" width="45px" name={senderId.name} src={senderId.profilePicture} />
                            <Box>
                                <Text isTruncated title={senderId?.name} fontWeight={'500'}>{senderId?.name}</Text>
                                <Text fontSize={'12'}>({senderId?.role})</Text>
                            </Box>
                        </Flex>
                        {
                            !isMe &&
                            <Button onClick={redirectToDirects} variant={'outlined'} minW={'300px'} border={'1px solid black'} borderRadius={'7px'} display={'flex'} columnGap={'10px'}>
                                <FontAwesomeIcon icon={faCommentAlt} />
                                <Text fontWeight={'500'}>Message</Text>
                            </Button>
                        }
                    </PopoverContent>
                </Popover>
                {isMe && (
                    <Box
                        opacity={0}
                        transition="opacity 0.2s ease"
                        _groupHover={{ opacity: 1 }}
                        height={'fit-content'}
                        borderRadius={'12px'}
                        position={'absolute'}
                        zIndex={4}
                        top={'0px'}
                        right={'0px'}
                    >
                        <Menu isLazy >
                            <MenuButton
                                as={IconButton}
                                icon={<FontAwesomeIcon icon={faEllipsisV} />}
                                size="sm"
                                variant="ghost"
                                aria-label="Options"
                                p={1}
                                _hover={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '50%',
                                }}
                            />

                            <MenuList
                                minW="fit-content"
                                borderRadius="8px"
                                boxShadow="0 2px 5px rgba(0, 0, 0, 0.15)"
                                position={'absolute'}
                                zIndex={6}
                                top={'-70px'}
                                right={'5px'}
                            >
                                {
                                    !editingModeActive && <MenuItem
                                        fontSize="sm"
                                        _hover={{
                                            background: 'rgba(0, 0, 0, 0.05)',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleEditMessage(_id)}
                                    >
                                        <FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px' }} />
                                        Edit
                                    </MenuItem>
                                }


                                {/* Delete Menu Item */}
                                <MenuItem
                                    fontSize="sm"
                                    color="red.500"
                                    _hover={{
                                        background: 'rgba(255, 0, 0, 0.1)',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleDeleteMessage(_id)}
                                >
                                    <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                                    Delete
                                </MenuItem>

                            </MenuList>
                        </Menu>


                    </Box>
                )
                }
            </Box >
            {
                type === "CALL" &&
                <CallStatusMessage
                    key={_id}
                    _id={_id}
                    messageTime={messageTime}
                    callStatus={callStatus}
                    roomId={roomId}
                    callProvider={callProvider}
                    duration={duration}
                />
            }
            {
                Array.isArray(fileURL) &&
                <Modal isOpen={isDocViewerOpen} size={'full'}>
                    <ModalOverlay>
                        <ModalContent background={"none"}>
                            <ModalBody>
                                <Box display={'flex'}
                                    justifyContent={'end'}
                                    alignItems={'center'}
                                >
                                    {/* <Button
                                        bg={'blackAlpha.600'}
                                        variant={'unstyled'}
                                        p={2}
                                        borderRadius={'100%'}
                                        display={'flex'}
                                        justifyContent={'end'}
                                        alignItems={'center'}
                                        color={'white'}
                                        onClick={() => setIsDocViewerOpen(false)}
                                    >
                                        <IconX />
                                    </Button> */}
                                </Box>
                                <FileViewer urls={fileURL} setIsDocViewerOpen={setIsDocViewerOpen} />
                                {/* <DocViewer
                                    documents={fileURL}
                                    pluginRenderers={[DocViewerRenderers]}
                                    activeDocument={activeDoc}
                                    onDocumentChange={setActiveDoc}
                                /> */}
                            </ModalBody>
                        </ModalContent>
                    </ModalOverlay>
                </Modal>
            }
        </Box >
    );
}, (pp, np) => _.isEqual(pp, np));
